import { useEffect, type ReactNode } from 'react';
import {
  ActionIcon,
  Anchor,
  Box,
  Breadcrumbs,
  Button,
  Group,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useConsoleNav } from '../shell/nav';
import {
  AutoGrid,
  ColLabel,
  ConsoleTable,
  EstatePill,
  MeterRow,
  NotConnected,
  Pill,
  ProviderPill,
  Row,
  SectionCard,
  TableMessage,
  uiClasses,
  StatSkeleton,
  ChartSkeleton,
  RowsSkeleton,
} from '../ui';
import { dateOnly, dateTime, expiringSoon, listCost, num, pct, tokens as fmtTokens } from '../ui/format';
import { tokens as T } from '../theme/tokens';
import {
  useDeploymentsStore,
  useEstatesStore,
  useTenantKeysStore,
  useTenantsStore,
  useTokensStore,
} from '../stores/dataStores';
import { isUnconfigured, rowsOf } from '../stores/collection';
import { panelPhase, useAnalyticsStore, type Panel } from '../stores/analyticsStore';
import { useUIStore } from '../stores/uiStore';
import { effectiveBillingMode, tenantKey } from '../data/types';
import { LegendSwatch, MODEL_COLOR, ThroughputChart } from '../features/charts';
import { getSources } from '../stores/sources';
import { NO_WRITE, READ_ONLY } from './PoolPage';
import { useMemoryStats } from '../features/memory/useMemoryStats';

const POOL_COLS = [
  { key: 'provider', label: 'Provider', w: 120 },
  { key: 'kind', label: 'Kind', w: 100 },
  { key: 'ref', label: 'Sealed reference' },
  { key: 'updated', label: 'Last rotated', w: 150 },
  { key: 'enabled', label: 'Enabled', w: 110 },
];

const DEPLOY_COLS = [
  { key: 'deployment', label: 'Deployment' },
  { key: 'billing', label: 'Billing', w: 120 },
  { key: 'expires', label: 'Token expires', w: 150 },
  { key: 'fingerprint', label: 'Fingerprint', w: 120 },
  { key: 'actions', w: 90 },
];

export function TenantPage() {
  const { estateId = '', tenantId = '' } = useParams();
  const navigate = useConsoleNav();
  const key = tenantKey(estateId, tenantId);

  const estates = useEstatesStore((s) => s.data);
  const tenants = rowsOf(useTenantsStore((s) => s.data));
  const deployments = useDeploymentsStore((s) => s.data);
  const openDeployments = useDeploymentsStore((s) => s.open);
  const tokensCol = useTokensStore((s) => s.data);
  const openTokens = useTokensStore((s) => s.open);
  const closeDeployments = useDeploymentsStore((s) => s.close);
  const closeTokens = useTokensStore((s) => s.close);
  const tenantKeys = useTenantKeysStore((s) => s.data);
  const loadTenantKeys = useTenantKeysStore((s) => s.load);
  const a = useAnalyticsStore();
  const ui = useUIStore();
  const canMutate = getSources().canMutate;

  useEffect(() => {
    openDeployments(estateId);
    openTokens(estateId);
    loadTenantKeys({ estateId, tenantId });
    a.load({ range: ui.range, estateId, tenantId });
    return () => {
      closeDeployments();
      closeTokens();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estateId, tenantId, ui.range]);

  const estate = estates.byId[estateId];
  const tenant = tenants.find((t) => t.key === key);
  // BOTH halves. Filtering on tenantId alone puts sw/acme's deployments under
  // am/acme's heading for as long as the new estate's watch is still loading —
  // and forever if it fails.
  const deployRows = rowsOf(deployments).filter(
    (d) => d.estateId === estateId && d.tenantId === tenantId,
  );
  const tokenRows = rowsOf(tokensCol);
  const keyRows = rowsOf(tenantKeys).filter(
    (k) => k.estateId === estateId && k.tenantId === tenantId,
  );
  const tokenFor = (id: string) => tokenRows.find((t) => t.deploymentId === id && !t.revokedAt);

  if (!tenant) {
    return (
      <Stack gap={16}>
        <Title order={2}>{tenantId}</Title>
        <NotConnected
          what="this tenant"
          detail="The router has no tenant entity — a tenant is a string carried on tokens, deployments and usage rows. This page will be derived from those."
        />
      </Stack>
    );
  }

  const scopedIn = ui.scope === key;
  const totals = a.totals;
  // Same rule as the overview: a skeleton stands in only while a panel has
  // never answered. Saying "not wired" during a query is a claim about the
  // backend, and a slow warehouse is not a missing route.
  const pending = (panel: Panel, data: unknown) =>
    panelPhase(data, a.errors[panel]) === 'pending';

  return (
    <Stack gap={16}>
      <Breadcrumbs separator="/" styles={{ separator: { color: T.ink4, marginInline: 8 } }}>
        <Anchor c={T.ink4} fw={700} fz={12} onClick={() => navigate('/estates')}>
          Estates
        </Anchor>
        <Anchor c={T.ink4} fw={700} fz={12} onClick={() => navigate(`/estates/${estateId}`)}>
          {estate?.displayName ?? estateId}
        </Anchor>
        <Text fz={12} fw={700} c={T.ink}>
          {tenant.displayName}
        </Text>
      </Breadcrumbs>

      <Group justify="space-between" align="center" gap={20} wrap="wrap">
        <Group gap={14} wrap="nowrap" style={{ minWidth: 0 }}>
          <ActionIcon
            variant="default"
            radius={999}
            size={36}
            onClick={() => navigate(`/estates/${estateId}`)}
          >
            ←
          </ActionIcon>
          <Title order={2}>{tenant.displayName}</Title>
          <EstatePill estate={estateId} size="xl" />
          <Pill tone="ok" size="xl">
            {tenant.status}
          </Pill>
        </Group>
        <Group gap={12}>
          <Button
            color="dark"
            onClick={() => ui.setScope(scopedIn ? null : key)}
          >
            {scopedIn ? 'Scoped in ✓' : 'Switch into tenant'}
          </Button>
          <Button onClick={() => ui.openModal({ kind: 'tenantKey', estateId, tenantId })}>
            Add provider key
          </Button>
          <Button
            variant="default"
            onClick={() => ui.openModal({ kind: 'mintToken', estateId, tenantId })}
          >
            Mint token
          </Button>
        </Group>
      </Group>
      <Text ff="monospace" fz={13} c={T.ink5} ml={50} mt={-8}>
        {key}
      </Text>

      <AutoGrid min={240} gap={16}>
        <SectionCard p="18px 20px">
          <ColLabel>Resolution order</ColLabel>
          <Stack gap={10} mt={14}>
            <Rung
              n={1}
              on={keyRows.length > 0}
              label="Tenant credential pool"
              detail={
                keyRows.length
                  ? `${keyRows.length} sealed key${keyRows.length === 1 ? '' : 's'}, under this tenant's DEK`
                  : 'not configured — skipped'
              }
              color={keyRows.length ? T.teal : T.toggleOff}
            />
            <Rung
              n={2}
              on
              label={`${estate?.displayName ?? estateId} pool`}
              detail="shared keys, plus any reserved to this estate"
              color={T.coral}
            />
            <Rung
              n={3}
              on
              label="Router process credentials"
              detail="Secret Manager, ADC for Gemini"
              color={T.faint}
            />
          </Stack>
        </SectionCard>

        <SectionCard p="18px 20px">
          <ColLabel>Usage · {ui.range} (ground truth)</ColLabel>
          <AutoGrid min={110} gap={12} mt={14}>
            <Stat
              label="Completions"
              value={
                totals ? num(totals.completions) : pending('totals', a.totals) ? <StatSkeleton /> : '—'
              }
            />
            <Stat
              label="Cost · list price"
              value={
                totals
                  ? listCost(totals.listCostUsd)
                  : pending('totals', a.totals)
                    ? <StatSkeleton />
                    : '—'
              }
            />
            <Stat
              label="Default billing mode"
              value={tenant.billingMode}
              small
              color={tenant.billingMode === 'billable' ? T.coralDeep : T.ink3}
              note="deployments carry the real one"
            />
            <Stat label="Envelope lane" value={tenant.lane} small />
          </AutoGrid>
        </SectionCard>

        <SectionCard p="18px 20px">
          <ColLabel>Rate limits</ColLabel>
          <AutoGrid min={110} gap={12} mt={14}>
            <Stat label="Billable / min" value={String(estate?.limits.billablePerMinute ?? '—')} />
            <Stat
              label="Attributed / min"
              value={String(estate?.limits.attributedPerMinute ?? '—')}
            />
          </AutoGrid>
          <Text fz={12} c={T.ink4} lh={1.55} mt={12}>
            Shown from the estate. The limiter keys on estate, deployment and subject — there is no
            tenant-level override to display.
          </Text>
        </SectionCard>

        <SectionCard p="18px 20px">
          <ColLabel>Memory</ColLabel>
          <MemorySummary estateId={estateId} tenantId={tenantId} memoryOn={estate?.memory?.mode === 'on'} />
        </SectionCard>
      </AutoGrid>

      <SectionCard
        title="Token throughput"
        subtitle={`${tenant.displayName} · daily · ${ui.range}`}
        right={
          a.throughput ? (
            <Group gap={14} wrap="wrap">
              <LegendSwatch color={T.coral} label="Tokens in" value={fmtTokens(a.throughput.totals.tokensIn)} />
              <LegendSwatch color={T.teal} label="Tokens out" value={fmtTokens(a.throughput.totals.tokensOut)} />
              <LegendSwatch color={T.violet} dashed label="Cache write" value={fmtTokens(a.throughput.totals.cacheWrite)} />
              <LegendSwatch color={T.marigold} dashed label="Cache read" value={fmtTokens(a.throughput.totals.cacheRead)} />
            </Group>
          ) : null
        }
      >
        <Box mt={16}>
          {a.throughput ? (
            <ThroughputChart data={a.throughput} height={150} />
          ) : pending('throughput', a.throughput) ? (
            <ChartSkeleton height={150} />
          ) : (
            <Text fz={13} c={T.ink4}>
              No series — the analytics warehouse is not wired.
            </Text>
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Tokens by model"
        subtitle="Keyed on the request's pinned model id — what billing keys on"
      >
        <Stack gap={13} mt={16}>
          {pending('models', a.models) ? <RowsSkeleton rows={3} height={26} /> : null}
          {(a.models?.models ?? []).map((m) => (
            <MeterRow
              key={m.model}
              value={m.share * 100}
              color={MODEL_COLOR[m.model] ?? T.faint}
              left={
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Box
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: MODEL_COLOR[m.model] ?? T.faint,
                    }}
                  />
                  <Text ff="monospace" fz={13} c={T.ink2} truncate>
                    {m.model}
                  </Text>
                </Group>
              }
              right={
                <Group gap={10} align="baseline" wrap="nowrap">
                  <Text fz={13} fw={800}>
                    {fmtTokens(m.tokens)}
                  </Text>
                  <Text fz={12} c={T.ink5} ta="right" style={{ minWidth: 30 }}>
                    {pct(m.share)}
                  </Text>
                </Group>
              }
            />
          ))}
        </Stack>
      </SectionCard>

      <SectionCard
        p={0}
        title={undefined}
      >
        <Group justify="space-between" gap={14} wrap="wrap" p="16px 20px">
          <Box>
            <Text fz={16} fw={800}>
              Tenant credential pool{' '}
              <Text component="span" fz={12} fw={600} c={T.ink5}>
                optional
              </Text>
            </Text>
            <Text fz={12} c={T.ink4} mt={3}>
              Rung 1 — consulted before this estate's shared pool. One key per provider today, not a
              rotating pool.
            </Text>
          </Box>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => ui.openModal({ kind: 'tenantKey', estateId, tenantId })}
          >
            Add key
          </Button>
        </Group>
        <ConsoleTable cols={POOL_COLS} minWidth={1000}>
          {keyRows.map((k) => (
            <Row key={k.id}>
              <Table.Td>
                <ProviderPill provider={k.provider} />
              </Table.Td>
              <Table.Td>
                <Text fz={12} fw={600} c={T.ink3}>
                  API key
                </Text>
              </Table.Td>
              <Table.Td>
                <Text ff="monospace" fz={12} c={T.ink3} truncate>
                  enc:v1:{k.id}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz={13} c={T.ink2}>
                  {dateTime(k.updatedAt)}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={8} wrap="nowrap">
                  <Switch
                    checked
                    readOnly
                    title={canMutate ? undefined : READ_ONLY}
                    classNames={{ input: uiClasses.switchInput, track: uiClasses.switchTrack }}
                    styles={{
                      root: {
                        '--switch-height': '22px',
                        '--switch-width': '40px',
                        '--switch-thumb-size': '16px',
                      },
                    }}
                    aria-label="Enabled"
                  />
                  <Text
                    fz={13}
                    fw={700}
                    c={canMutate ? T.danger : T.faint}
                    title={canMutate ? undefined : NO_WRITE}
                  >
                    Rotate
                  </Text>
                </Group>
              </Table.Td>
            </Row>
          ))}
          {keyRows.length === 0 ? (
            <TableMessage
              span={POOL_COLS.length}
              title={
                isUnconfigured(tenantKeys)
                  ? 'Not connected — sealed keys are not browser-readable'
                  : `No tenant pool — this tenant draws from ${estate?.displayName ?? estateId}'s shared keys`
              }
              detail={
                isUnconfigured(tenantKeys)
                  ? 'llm_provider_keys carries keyRef, and a Firestore rule grants a whole document rather than a field — so this reads through the control plane.'
                  : "Add a key to give this tenant its own credentials, sealed under its own DEK. Useful when a customer brings a subscription, or contract terms forbid shared credentials."
              }
            />
          ) : null}
        </ConsoleTable>
        <Text fz={12} c={T.ink4} lh={1.6} p="12px 20px" style={{ borderTop: `1px solid ${T.lineSoft}` }}>
          Sealed with credvault under this tenant's DEK, AAD = tenant id. The document id is a
          digest of{' '}
          <Text component="span" ff="monospace">
            ({estateId}, {tenantId}, provider)
          </Text>
          , not the joined tuple — a key id that led with the estate would put every one of its
          keys in a single Firestore key range. A read verifies the stored tuple before unsealing,
          so a digest collision fails closed rather than opening another tenant's key, and material
          is never returned to this console.
        </Text>
      </SectionCard>

      <SectionCard p={0}>
        <Group justify="space-between" p="16px 20px">
          <Text fz={16} fw={800}>
            Deployments &amp; router tokens
          </Text>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => ui.openModal({ kind: 'mintToken', estateId, tenantId })}
          >
            Mint token
          </Button>
        </Group>
        <ConsoleTable cols={DEPLOY_COLS} minWidth={900}>
          {deployRows.map((d) => {
            const tok = tokenFor(d.id);
            const mode = tok ? effectiveBillingMode(tok) : '—';
            return (
              <Row key={d.id}>
                <Table.Td>
                  <Text fz={14} fw={700}>
                    {d.label}
                  </Text>
                  <Text ff="monospace" fz={12} c={T.ink5} mt={2}>
                    {d.id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} fw={700} c={mode === 'billable' ? T.coralDeep : T.ink3}>
                    {mode}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text fz={13} c={expiringSoon(tok?.expiresAt) ? T.danger : T.ink2}>
                    {dateOnly(tok?.expiresAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" fz={12} c={T.ink3}>
                    {tok?.tokenSha256 ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    fz={13}
                    fw={700}
                    c={canMutate ? T.danger : T.faint}
                    ta="right"
                    title={canMutate ? undefined : NO_WRITE}
                  >
                    Revoke
                  </Text>
                </Table.Td>
              </Row>
            );
          })}
          {deployRows.length === 0 ? (
            <TableMessage span={DEPLOY_COLS.length} title="No deployments for this tenant" />
          ) : null}
        </ConsoleTable>
      </SectionCard>
    </Stack>
  );
}

function Rung({
  n,
  label,
  detail,
  color,
  on,
}: {
  n: number;
  label: string;
  detail: string;
  color: string;
  on: boolean;
}) {
  return (
    <Group gap={10} wrap="nowrap" align="center">
      <Box
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          background: color,
          color: '#fff',
          fontSize: 12,
          fontWeight: 800,
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        {n}
      </Box>
      <Text fz={14} fw={600} c={on ? T.ink : T.ink5}>
        {label}
        <Text component="span" c={T.ink5} fw={500}>
          {' '}
          — {detail}
        </Text>
      </Text>
    </Group>
  );
}

function Stat({
  label,
  value,
  small,
  color,
  note,
}: {
  label: string;
  value: ReactNode;
  small?: boolean;
  color?: string;
  note?: string;
}) {
  return (
    <Box>
      <Text fz={13} c={T.ink3}>
        {label}
      </Text>
      {/* A div: a pending value is a StatSkeleton, which is a div. See StatCard. */}
      <Text component="div" fz={small ? 16 : 24} fw={small ? 700 : 800} mt={small ? 6 : 2} c={color}>
        {value}
      </Text>
      {note ? (
        <Text fz={11} c={T.ink5} mt={2}>
          {note}
        </Text>
      ) : null}
    </Box>
  );
}

/** The tenant's memory counts, from the admin API — not a watch, because
 *  router_memories is closed to browsers (DR-75). Three honest states beyond
 *  the counts: not connected, off for the estate, never remembered. */
function MemorySummary({ estateId, tenantId, memoryOn }: { estateId: string; tenantId: string; memoryOn: boolean }) {
  const navigate = useConsoleNav();
  const stats = useMemoryStats(estateId, tenantId);
  if (stats.phase === 'unconfigured') {
    return (
      <Text fz={12} c={T.ink4} lh={1.55} mt={14}>
        Not connected — memory has no admin route on this instance.
      </Text>
    );
  }
  if (!memoryOn) {
    return (
      <Text fz={12} c={T.ink4} lh={1.55} mt={14}>
        Memory is off for this estate; every /v1/memory/* call answers 403. Turn it on with{' '}
        <code>haus memory set --estate {estateId} --mode on --apply</code>.
      </Text>
    );
  }
  if (stats.phase === 'error') {
    return (
      <Text fz={12} c={T.coralDeep} lh={1.55} mt={14}>
        {stats.message}
      </Text>
    );
  }
  const ready = stats.phase === 'ready' ? stats.stats : undefined;
  return (
    <>
      <AutoGrid min={110} gap={12} mt={14}>
        <Stat label="Active" value={ready ? num(ready.active) : <StatSkeleton />} />
        <Stat label="Superseded" value={ready ? num(ready.superseded) : <StatSkeleton />} small />
        <Stat label="Forgotten" value={ready ? num(ready.deleted) : <StatSkeleton />} small />
      </AutoGrid>
      {ready && ready.active + ready.superseded + ready.deleted === 0 ? (
        <Text fz={12} c={T.ink4} lh={1.55} mt={12}>
          No memories — this tenant has never called /v1/memory/remember.
        </Text>
      ) : null}
      <Button variant="default" size="xs" mt={12} onClick={() => navigate(`/estates/${estateId}/tenants/${tenantId}/memory`)}>
        Open memory browser →
      </Button>
    </>
  );
}
